/**
 * Minimal LRU cache with per-entry TTL used by SSR preload stores.
 *
 * Long-lived Node processes that serve crawler traffic must not grow their
 * preloaded-data Maps indefinitely. This cache bounds size by an LRU policy
 * (oldest insertion evicted first) and invalidates stale entries by TTL so
 * that CMS updates eventually propagate without a pod restart.
 *
 * The public shape is a deliberate subset of Map so call sites can swap it
 * in with no changes.
 */

export interface PreloadCacheOptions {
  /** Maximum number of entries to retain. Oldest-touched is evicted first. */
  max: number;
  /** Per-entry lifetime in milliseconds. */
  ttlMs: number;
}

interface Entry<V> {
  value: V;
  expiresAt: number;
}

export class PreloadCache<V> {
  private readonly max: number;
  private readonly ttlMs: number;
  private readonly store = new Map<string, Entry<V>>();

  constructor({ max, ttlMs }: PreloadCacheOptions) {
    this.max = max;
    this.ttlMs = ttlMs;
  }

  get(key: string): V | undefined {
    const entry = this.store.get(key);
    if (!entry) {
      return undefined;
    }
    if (entry.expiresAt <= Date.now()) {
      this.store.delete(key);
      return undefined;
    }
    // Mark as most-recently-used.
    this.store.delete(key);
    this.store.set(key, entry);
    return entry.value;
  }

  has(key: string): boolean {
    return this.get(key) !== undefined;
  }

  set(key: string, value: V): void {
    if (this.store.has(key)) {
      this.store.delete(key);
    } else if (this.store.size >= this.max) {
      const oldest = this.store.keys().next().value;
      if (oldest !== undefined) {
        this.store.delete(oldest);
      }
    }
    this.store.set(key, { value, expiresAt: Date.now() + this.ttlMs });
  }

  delete(key: string): void {
    this.store.delete(key);
  }
}
